/**
 * Mock Data Banner
 * 
 * Displayed when USE_MOCK = true to clearly indicate that the journey
 * data is fake/hardcoded and not based on user inputs.
 * 
 * This prevents confusion during development and testing.
 */

import { AlertTriangle, Code, X } from 'lucide-react';
import { useState } from 'react';
import { USE_MOCK } from '@/lib/mocks/mockJourneyResponse';

interface MockDataBannerProps {
  className?: string;
}

export function MockDataBanner({ className }: MockDataBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  // Don't render if mock mode is off or banner was dismissed
  if (!USE_MOCK || dismissed) {
    return null;
  }

  return (
    <div
      className={`relative bg-amber-50 border-2 border-amber-300 border-dashed rounded-lg p-4 ${className}`}
    >
      {/* Dismiss button */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded"
        aria-label="Dismiss banner"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 bg-amber-100 rounded-full">
          <Code className="w-5 h-5 text-amber-600" />
        </div>
        
        <div className="flex-1 pr-6">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-amber-800">
              Mock Data Mode Active
            </h3>
          </div>
          
          <p className="text-sm text-amber-700 mb-2">
            You're viewing <strong>hardcoded test data</strong>, not calculations based on your inputs. 
            This is for UI development only.
          </p>
          
          <div className="text-xs text-amber-600 bg-amber-100 rounded px-2 py-1 inline-block font-mono">
            Set <code>USE_MOCK = false</code> in{' '}
            <code>src/lib/mocks/mockJourneyResponse.ts</code>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MockDataBanner;
